import React from 'react';
import './styles.css';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import { ERROR_TYPES, getErrorMessage, getShortErrorCopy, getErrorTitle, getErrorBody } from './errorMessages.constants';

// Error icon (Figma node 11228-3543) — shared by every error type/variant. Inlined as
// JSX (rather than an <img src>) so its fill can be controlled via CSS if ever needed,
// and so no extra network/asset request is added for what is effectively a static
// design-system icon.
const ErrorIcon = () => (
  <svg
    className="api-error-banner__icon-svg"
    xmlns="http://www.w3.org/2000/svg"
    width="64"
    height="64"
    viewBox="0 0 64 64"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <g clipPath="url(#clip0_11229_4449)">
      <mask
        id="mask0_11229_4449"
        style={{ maskType: 'luminance' }}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="64"
        height="64"
      >
        <path d="M64 0H0V64H64V0Z" fill="white" />
      </mask>
      <g mask="url(#mask0_11229_4449)">
        <path d="M33.3421 45.2637H30.6582V47.9475H33.3421V45.2637Z" fill="#7D3F98" />
        <path d="M33.3421 23.8965H30.6582V42.5804H33.3421V23.8965Z" fill="#7D3F98" />
        <path
          d="M61.3164 58.5803H2.6841C2.21222 58.5844 1.74786 58.462 1.33933 58.2258C0.930807 57.9896 0.593044 57.6482 0.361191 57.2372C0.129339 56.8262 0.0118679 56.3606 0.0210027 55.8887C0.0301375 55.4169 0.165544 54.9562 0.413132 54.5545L29.7293 6.55447C29.9815 6.18467 30.3202 5.88206 30.716 5.67293C31.1117 5.4638 31.5526 5.35449 32.0002 5.35449C32.4479 5.35449 32.8887 5.4638 33.2845 5.67293C33.6803 5.88206 34.019 6.18467 34.2712 6.55447L63.5873 54.5545C63.8349 54.9562 63.9703 55.4169 63.9795 55.8887C63.9886 56.3606 63.8711 56.8262 63.6393 57.2372C63.4074 57.6482 63.0696 57.9896 62.6611 58.2258C62.2526 58.462 61.7882 58.5844 61.3164 58.5803ZM32.0002 7.8964L2.6841 55.8964H61.368L32.0002 7.8964Z"
          fill="#7D3F98"
        />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_11229_4449">
        <rect width="64" height="64" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

/**
 * Shared banner for surfacing the four integration-error categories called out in
 * the "Specific error messages for Provider Search 2.0" story: APIC/MCS backend,
 * Google location services, Akorbi translation, and SMTP email.
 *
 * See reports/SPECIFIC_ERROR_MESSAGES_PLAN.md for the full plan/copy options.
 *
 * @param {Object} props
 * @param {'api'|'location'|'translation'|'email'} props.type - Which error category to render.
 * @param {'full'|'short'|'panel'} [props.variant='full'] - 'full' renders the large white card
 *   design (Figma node 11228-3543: 64px icon, title + body two-line layout) meant to
 *   sit inline in the main content column, below the page header, pushing the rest of
 *   the page down. 'short' renders a compact single-line message for tighter spaces.
 *   'panel' renders the same card layout (icon + title + body) as 'full' but scaled
 *   down to fit narrower containers like the Location side panel, without the wide
 *   page-level horizontal margins the 'full' variant uses.
 * @param {boolean} [props.showContactUs=true] - Whether to render the "Contact us" link.
 * @param {React.ReactNode} [props.action] - Optional extra action rendered after the
 *   message (e.g. a "Download results" button for the email fallback).
 * @param {boolean} [props.dismissible=false] - Whether to show a close (x) button.
 * @param {() => void} [props.onDismiss] - Called when the dismiss button is clicked.
 */
const ApiErrorBanner = ({
  type,
  variant = 'full',
  showContactUs = true,
  action = null,
  dismissible = false,
  onDismiss,
}) => {
  const { getErrorContactUsUrl } = usePlanFeatures();

  if (!type || !Object.values(ERROR_TYPES).includes(type)) {
    console.warn(`ApiErrorBanner: unknown error type "${type}"`);
    return null;
  }

  const contactUsUrl = showContactUs ? getErrorContactUsUrl() : null;

  const ContactLink = contactUsUrl ? (
    <a
      className="api-error-banner__contact-link"
      href={contactUsUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      Need help? Contact us.
    </a>
  ) : null;

  if (variant === 'short') {
    const message = getShortErrorCopy(type);
    return (
      <div className={`api-error-banner api-error-banner--short api-error-banner--${type}`} role="alert">
        <span className="api-error-banner__icon api-error-banner__icon--short" aria-hidden="true">!</span>
        <span className="api-error-banner__message">
          {message}
          {ContactLink && <>{' '}{ContactLink}</>}
        </span>
        {action && <span className="api-error-banner__action">{action}</span>}
        {dismissible && (
          <button
            type="button"
            className="api-error-banner__dismiss"
            aria-label="Dismiss message"
            onClick={onDismiss}
          >
            &times;
          </button>
        )}
      </div>
    );
  }

  const title = getErrorTitle(type);
  const body = getErrorBody(type) || getErrorMessage(type);
  const cardVariantClass = variant === 'panel' ? 'api-error-banner--panel' : 'api-error-banner--card';

  return (
    <div className={`api-error-banner ${cardVariantClass} api-error-banner--${type}`} role="alert">
      <span className="api-error-banner__icon-wrap" aria-hidden="true">
        <ErrorIcon />
      </span>
      <div className="api-error-banner__content">
        <p className="api-error-banner__title">{title}</p>
        <p className="api-error-banner__body">
          {body}
          {ContactLink && <>{' '}{ContactLink}</>}
        </p>
        {action && <div className="api-error-banner__action">{action}</div>}
      </div>
      {dismissible && (
        <button
          type="button"
          className="api-error-banner__dismiss"
          aria-label="Dismiss message"
          onClick={onDismiss}
        >
          &times;
        </button>
      )}
    </div>
  );
};

export default ApiErrorBanner;
